import { useState, useEffect } from 'react';
import { Phone, Mail, Send, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { useMetadata } from '../hooks/useMetadata';

export default function Contact() {
  const [cmsPage, setCmsPage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot felt
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/kontakt')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente kontakt fra CMS');
        return res.json();
      })
      .then((data) => {
        if (data && data.collectionName === 'pages') {
          setCmsPage(data);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  // Brug metadata fra CMS eller fallback
  const title = cmsPage?.meta_title || 'Kontakt - FLOW Transport & Logistik ApS';
  const description =
    cmsPage?.meta_description ||
    'Kontakt FLOW Transport & Logistik ApS i Vanløse. Ring på 22 28 93 10 or send en mail til kontakt@flowtl.dk for et uforpligtende tilbud.';

  useMetadata(title, description);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Simpel validering
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.description.trim()) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst alle påkrævede felter.'
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl ved afsendelse af formularen. Prøv igen senere.');
      }
    } catch (err) {
      console.error('Fejl ved formularafsendelse:', err);
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der skete en netværksfejl. Kontroller din internetforbindelse og prøv igen.'
      });
    }
  };

  return (
    <div className="w-full bg-slate-50 font-sans">
      {/* Side Header */}
      <section className="bg-brand-dark text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#0056b3_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight">
            Kontakt os
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto font-sans">
            Har du spørgsmål, eller ønsker du et uforpligtende tilbud? Vi sidder klar til at hjælpe dig med din næste transportopgave.
          </p>
        </div>
      </section>

      {/* Introduktion fra CMS */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="text-slate-600 text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: cmsPage?.content || '<p>Har du spørgsmål eller ønsker du et uforpligtende tilbud? Kontakt os i dag via vores formular, telefon eller e-mail. Vi står klar til at hjælpe dig med din næste transportopgave.</p>' }}
          />
        </div>
      </section>

      {/* Hovedindhold */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Venstre kolonne: Kontaktoplysninger (5 cols) */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-white p-8 rounded-2xl shadow-3d-card border border-slate-100 space-y-6">
                <h2 className="text-2xl font-serif font-bold text-brand-primary border-b border-slate-100 pb-4">
                  Kontaktoplysninger
                </h2>
                
                <div className="space-y-6">
                  {/* Saad El Mahdi */}
                  <div className="border-b border-slate-100 pb-6 last:border-0 last:pb-0">
                    <div className="text-base font-bold text-brand-dark">Saad El Mahdi</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Vognmand</div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4 text-brand-primary shrink-0" />
                        <a href="tel:+4529713434" className="hover:text-brand-primary transition-colors whitespace-nowrap">
                          29 71 34 34
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="h-4 w-4 text-brand-primary shrink-0" />
                        <a href="mailto:sem@flowtl.dk" className="hover:text-brand-primary transition-colors whitespace-nowrap">
                          sem@flowtl.dk
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Camilla El Mahdi */}
                  <div className="pb-2">
                    <div className="text-base font-bold text-brand-dark">Camilla El Mahdi</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Kontor</div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4 text-brand-primary shrink-0" />
                        <a href="tel:+4522289310" className="hover:text-brand-primary transition-colors whitespace-nowrap">
                          22 28 93 10
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="h-4 w-4 text-brand-primary shrink-0" />
                        <a href="mailto:kontakt@flowtl.dk" className="hover:text-brand-primary transition-colors whitespace-nowrap">
                          kontakt@flowtl.dk
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100 space-y-2 text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>CVR-nummer: <strong className="text-slate-700">46483057</strong></span>
                  </div>
                </div>
              </div>

              {/* Åbningstider */}
              <div className="bg-brand-dark text-white p-8 rounded-2xl space-y-6 shadow-3d-card">
                <h3 className="text-xl font-serif font-bold border-b border-white/10 pb-4">Åbningstider</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="font-bold text-slate-300">Mandag - fredag</div>
                    <div className="text-lg font-semibold mt-0.5">07:00-18:00</div>
                  </div>
                  <div>
                    <div className="font-bold text-slate-300">Weekend</div>
                    <div className="text-lg font-semibold mt-0.5">Efter aftale</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Højre kolonne: Kontaktformular (7 cols) */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 sm:p-10 rounded-2xl shadow-3d-card border border-slate-100">
                <h2 className="text-2xl font-serif font-bold text-brand-primary mb-6">
                  Send os en besked
                </h2>

                {status.success ? (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-2xl space-y-3">
                    <div className="flex items-center gap-2.5">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                      <h3 className="font-bold text-lg">Tak for din henvendelse!</h3>
                    </div>
                    <p className="text-sm leading-relaxed">
                      Vi har modtaget din besked og vil vende tilbage til dig så hurtigt som muligt — typisk inden for 2 timer.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot felt - ABSOLUT SKJULT for mennesker */}
                    <div className="sr-only absolute -top-96 -left-96">
                      <label htmlFor="website_url">Efterlad dette felt tomt</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        autoComplete="off"
                      />
                    </div>

                    {status.error && (
                      <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-lg flex items-start gap-2.5 text-sm">
                        <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                        <span>{status.error}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* Navn */}
                      <div className="space-y-2">
                        <label htmlFor="name" className="block text-sm font-bold text-brand-dark">
                          Navn <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm transition-all"
                          placeholder="Dit fulde navn"
                        />
                      </div>

                      {/* Telefonnummer */}
                      <div className="space-y-2">
                        <label htmlFor="phone" className="block text-sm font-bold text-brand-dark">
                          Telefonnummer <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm transition-all"
                          placeholder="F.eks. 22 28 93 10"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-bold text-brand-dark">
                        E-mailadresse <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm transition-all"
                        placeholder="F.eks. navn@firma.dk"
                      />
                    </div>

                    {/* Beskrivelse */}
                    <div className="space-y-2">
                      <label htmlFor="description" className="block text-sm font-bold text-brand-dark">
                        Beskrivelse af opgaven <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required
                        rows={5}
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm transition-all resize-y"
                        placeholder="Beskriv din transport- eller logistikopgave (f.eks. afhentningssted, leveringssted, godstype, dimensioner)..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={status.submitting}
                      className="w-full inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary/95 disabled:bg-slate-400 text-white px-6 py-4 rounded-lg text-base font-bold uppercase tracking-wider transition-colors shadow hover:shadow-md cursor-pointer"
                    >
                      {status.submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Sender besked...
                        </>
                      ) : (
                        <>
                          Send besked
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
